import { zodResolver } from '@hookform/resolvers/zod'
import type { HttpError } from '@refinedev/core'
import { useCan, useInvalidate } from '@refinedev/core'
import { useForm } from '@refinedev/react-hook-form'
import type { Tenant } from '@sirius/contracts/admin'
import { tenantCreateInputSchema } from '@sirius/contracts/admin'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAdminClient } from '~/api/adminClientContext'
import { InheritableSelect } from '~/components/inheritableSelect'
import { Button } from '~/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  ADMIN_LANGUAGE_OPTIONS,
  inheritedAdminLanguageLabel,
  isAdminLanguage,
} from '~/i18n/adminLanguage'
import {
  FORM_RECORD_QUERY_OPTIONS,
  useRecordPending,
} from '~/lib/formRecordQuery'
import { USE_DEFAULT, changedOnly, fromOwn, toOwn } from '~/lib/inheritable'
import {
  LANGUAGE_NAMES,
  LANGUAGE_OPTIONS,
  isCabinetLanguage,
} from '~/pages/cabinets/cabinetLanguage'
import { SETTINGS_RESOURCE } from '~/providers/accessControl'
import { refreshMe } from '~/providers/sessionQueries'
import { useActiveTenant } from '~/tenancy/activeTenant'

/**
 * Owners change the name and the default languages (select values) only:
 * slug and status are staff's (the API refuses them).
 */
const generalSchema = tenantCreateInputSchema.pick({ name: true }).extend({
  defaultLanguageChoice: z.string(),
  adminLanguageChoice: z.string(),
})
type GeneralValues = z.input<typeof generalSchema>
type GeneralVariables = Pick<Tenant, 'name'> &
  Partial<Pick<Tenant, 'defaultLanguage' | 'adminLanguage'>>

/**
 * The host tenant's name and default language (the language of its cabinets
 * on Use default), which its owners (and staff) change; admins see them
 * read-only. The slug (the tenant's address) and status are not here: only
 * staff change them (Platform › Tenants).
 */
export function GeneralTab() {
  const tenant = useActiveTenant()
  const { t } = useTranslation()
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const invalidate = useInvalidate()
  const { data: access } = useCan({
    resource: SETTINGS_RESOURCE,
    action: 'edit',
  })
  const canEdit = access?.can === true

  const form = useForm<Tenant, HttpError, GeneralValues>({
    resolver: zodResolver(generalSchema),
    defaultValues: {
      name: '',
      defaultLanguageChoice: USE_DEFAULT,
      adminLanguageChoice: USE_DEFAULT,
    },
    refineCoreProps: {
      resource: 'tenants',
      action: 'edit',
      id: tenant.id,
      redirect: false,
      queryOptions: FORM_RECORD_QUERY_OPTIONS,
      successNotification: () => ({
        type: 'success',
        message: t('settingsPage.general.savedNotification'),
      }),
      // the header's tenant name comes from the tenant gate and the switcher;
      // cabinets on Use default show the default language they inherit; the
      // session names the panel language, which may be this tenant's now
      onMutationSuccess: async () => {
        await Promise.all([
          refreshMe(client, queryClient),
          queryClient.invalidateQueries({ queryKey: ['tenantGate'] }),
          queryClient.invalidateQueries({ queryKey: ['tenantSwitcher'] }),
          invalidate({ resource: 'tenants', invalidates: ['resourceAll'] }),
          invalidate({ resource: 'cabinets', invalidates: ['resourceAll'] }),
        ])
      },
    },
  })
  const { onFinish, formLoading, query } = form.refineCore
  // nothing may be typed or saved before the record has settled
  const pending = useRecordPending('edit', query)
  const record = query?.data?.data
  const { reset } = form

  // `defaultLanguageChoice` and `adminLanguageChoice` are no fields of the
  // record, so Refine can't fill them in; it must not: Refine would write the
  // record's `defaultLanguage` / `adminLanguage` (null for Use default) over
  // the select's `USE_DEFAULT`
  useEffect(() => {
    if (record !== undefined) {
      reset({
        name: record.name,
        defaultLanguageChoice: fromOwn(record.defaultLanguage),
        adminLanguageChoice: fromOwn(record.adminLanguage),
      })
    }
  }, [record, reset])

  if (query?.error) {
    return (
      <p
        role="alert"
        className="text-destructive text-sm"
      >
        {t('settingsPage.general.loadError', {
          message: query.error.message,
        })}
      </p>
    )
  }

  return (
    <Form {...form}>
      <form
        aria-busy={pending}
        // the form's own fields only, whatever else the record put into it;
        // the API records only what changed
        onSubmit={form.handleSubmit(
          ({ name, defaultLanguageChoice, adminLanguageChoice }) =>
            onFinish({
              name,
              ...changedOnly(
                'defaultLanguage',
                record?.defaultLanguage,
                toOwn(defaultLanguageChoice, isCabinetLanguage),
              ),
              ...changedOnly(
                'adminLanguage',
                record?.adminLanguage,
                toOwn(adminLanguageChoice, isAdminLanguage),
              ),
            } satisfies GeneralVariables as never),
        )}
        className="max-w-md space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsPage.general.name.label')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  readOnly={!canEdit}
                  disabled={pending}
                />
              </FormControl>
              <FormDescription>
                {canEdit
                  ? t('settingsPage.general.name.hintOwner')
                  : t('settingsPage.general.name.hintReadOnly')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="defaultLanguageChoice"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('settingsPage.general.defaultLanguage.label')}
              </FormLabel>
              <InheritableSelect
                aria-label={t('settingsPage.general.defaultLanguage.label')}
                value={field.value}
                // a select has no read-only state: admins get it disabled
                disabled={!canEdit || pending}
                onChange={field.onChange}
                options={LANGUAGE_OPTIONS}
                inheritedLabel={
                  record === undefined
                    ? undefined
                    : LANGUAGE_NAMES[record.inheritedDefaultLanguage]
                }
              />
              {/* admins read "Only the tenant's owners …" under the name */}
              <FormDescription>
                {t('settingsPage.general.defaultLanguage.hint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="adminLanguageChoice"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shell.adminLanguage.label')}</FormLabel>
              <InheritableSelect
                aria-label={t('shell.adminLanguage.label')}
                value={field.value}
                disabled={!canEdit || pending}
                onChange={field.onChange}
                options={ADMIN_LANGUAGE_OPTIONS}
                inheritedLabel={inheritedAdminLanguageLabel(
                  record?.inheritedAdminLanguage,
                  t('shell.adminLanguage.browser'),
                )}
              />
              <FormDescription>
                {t('shell.adminLanguage.tenantHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {canEdit && (
          <Button
            type="submit"
            disabled={formLoading || pending}
          >
            {t('settingsPage.general.save')}
          </Button>
        )}
      </form>
    </Form>
  )
}
